import * as React from "react";
import { useEffect, useState, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { DataGrid } from "@mui/x-data-grid";
import { Button } from "@mui/material";
import ControlPointIcon from "@mui/icons-material/ControlPoint";
import styled from "styled-components";
import moment from 'moment';

import { breadcrumbsActions } from "@store/breadcrumbs";
import { getAllBulkPurchase } from "@store/bulk-purchase-management";
import ApplicationPageTemplate from "../../../dashboard/components/ApplicationPageTemplate/ApplicationPageTemplate";
import { CountryList } from "@components/BulkPurchaseCountryList";
import GenerateVoucherModal from "./generateVoucher";
import roles from '@configs/roles';
import { SSO_WIDGET_STYLE_URL } from './constants';

const FilterActionContainer = styled.div`
	padding: 16px 16px 0 16px;
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: space-between;
`;

const FilterContainer = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
`;

const OptionContainer = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
`;

const TitleCell = styled.div`
	min-width: 100%;
	flex: 1;
	font-weight: 500;
`;

const ActionButtons = styled.div`
	display: flex;
	flex-direction: column;
	flex-wrap: nowrap;
	align-items: flex-start;
	padding: 8px 0;
	gap: 10px;
`;

const BulkPurchase = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	
	const userRoles = useSelector(state => state.auth.userRoles);
	const bulkPurchaseList = useSelector(state => state.bulkPurchaseList.bulkPurchaseList);	

	const [country, setCountry] = useState("my");
	const [openModal, setOpenModal] = useState(false);
	const [allowView, setAllowView] = useState(false);

	useEffect(() => {
		dispatch(
			breadcrumbsActions.setBreadcrumbs([
				{ label: "Home", url: "/" },
				{ label: "Bulk Purchase Management", url: "/bulk-purchase-management" },
			])
		);
	}, [dispatch]);

	useEffect(() => {
		if (userRoles) {
			if (userRoles.includes(roles.BULK_PURCHASE_ADMIN)) {
				setAllowView(true);
			} else {
				setAllowView(false)
			}
		}
	}, [userRoles]);

	useEffect(() => {
		fetchBulkPurchase();
	}, [allowView])

	useEffect(() => {
		fetchBulkPurchase()
	}, [country]);

	const fetchBulkPurchase = () => {
		if (allowView) dispatch(getAllBulkPurchase({ country }));
	}

	// bulk purchase columns
	const columns = [
		{
			field: "id",
			headerName: "Id",
			width: 220,
			renderCell: (params) => (
				<div>
					<TitleCell>{params.id}</TitleCell>
				</div>
			),
		},
		{ field: "productId", headerName: "Product Id", width: 200 },
		{ field: "referenceId", headerName: "Reference No", width: 200 },
		{ field: "country", headerName: "Country", width: 80 },
		{ field: "currency", headerName: "Currency", width: 100 },
		{ field: "amount", headerName: "Amount", width: 100 },
		{ field: "quantity", headerName: "Quantity", width: 100 },
		{ field: "customerEmail", headerName: "Customer Email", width: 200 },
		{ field: "businessEmail", headerName: "Business Email", width: 200 },
		{ 
			field: "createdAt",
			headerName: "CreatedAt",
			width: 200,
			renderCell: (params) => (
				<>{moment(params.value).format('YYYY-MM-DD HH:mm:ss')}</>
			),
		},
		{
			field: "actions",
			headerName: "Actions",
			width: 250,
			renderCell: (params) => (
				<ActionButtons>
					<Button
						variant="outlined"
						color="primary"
						size="small"
						onClick={() => {
							navigate(`/bulk-purcahse-management/view-vouchers/${params.id}`);
						}}
					>
						View Vouchers
					</Button>
				</ActionButtons>
			),
		},
	];

	const handleOpenModal = () => {
		setOpenModal(true);
	};

	const handleCloseModal = () => {
		setOpenModal(false);
	};

	const handleCountryChange = (country) => {
		if (!country) return;
		setCountry(country);
	};

	const handleAddNew = () => {
		fetchBulkPurchase();
	};

	return (
		<ApplicationPageTemplate title={"Bulk Purchase Management"} description={"Manage Bulk Purchase Voucher Creation"} pageReady={true}>
			{<link href={SSO_WIDGET_STYLE_URL} rel="stylesheet" />}
			<link href="https://fonts.googleapis.com/css?family=Roboto:400,500" rel="stylesheet" />
			{allowView ? 
				(
					<div style={{ height: 800, width: "100%" }}>
						<FilterActionContainer>
							<FilterContainer>
								<CountryList
									required={true}
									fullWidth={true}
									width="250px"
									margin="normal"
									onChange={handleCountryChange}
									placeholder="Select Country"
									id="country" value={country != null ? country : "my"}
									background="white"
									compactView
								/>
								<OptionContainer>
								</OptionContainer>
							</FilterContainer>
							<div>
								<Button variant="contained" color="primary" onClick={handleOpenModal} size="large" startIcon={<ControlPointIcon size="large" />} disableElevation>
									New Bulk Pruchase
								</Button>
							</div>
						</FilterActionContainer>
						<DataGrid
							sx={{ m: 2 }}
							getRowHeight={() => "auto"}
							columns={columns}
							rows={bulkPurchaseList}
							rowClassName="fixed-height-cell"
							rowHeight={90}
							pageSizeOptions={[15, 30, 50, 100]}
							initialState={{ 
								pagination: { paginationModel: { pageSize: 15 } },
								sorting: {sortModel: [{ field: 'createdAt', sort: 'desc' }]},
							}}						
						/>
						<GenerateVoucherModal
							isVisible={openModal}
							onClose={handleCloseModal}
							onCreate={handleAddNew}
							country={country != null ? country : "my"}
						/>
					</div>
				) : (<h1>You do not have permission to view the content</h1>)
			}
		</ApplicationPageTemplate>
	);
};

export default BulkPurchase;
